"use client"

import { useState, useEffect } from "react"
import { signup, updateAcademy } from "../comman/Academy.js"
import { fetchOnlyCoachData } from "../comman/Coach.js"

// Inline component definitions
const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">{children}</div>
    </div>
  )
}

const Input = ({ label, ...props }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <input
      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#12143F] focus:border-transparent"
      {...props}
    />
  </div>
)

const Select = ({ label, options, ...props }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <select
      className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#12143F] focus:border-transparent"
      {...props}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
)

const MultiSelect = ({ label, options, value, onChange }) => (
  <div className="mb-4">
    <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
    <div className="flex flex-wrap gap-2">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          onClick={() => onChange(option.value)}
          className={`px-3 py-1 rounded-full text-sm ${
            value.includes(option.value) ? "bg-[#12143F] text-white" : "bg-gray-200 text-gray-700"
          }`}
        >
          {option.label}
        </button>
      ))}
    </div>
  </div>
)

const AddAcademyModal = ({ isOpen, onClose, onAddAcademy, onUpdateAcademy, academyData }) => {
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    locationPin: "",
    timings: "",
    sports: [],
    availableDays: [],
    coach: "",
    pricing: "",
    password: "",
    confirmPassword: "",
    hasOwnAcademy: true,
  })

  const [coaches, setCoaches] = useState([])
  const [error, setError] = useState("")
  const [successMessage, setSuccessMessage] = useState("")

  const sportsOptions = [
    { value: "Football", label: "Football" },
    { value: "Basketball", label: "Basketball" },
    { value: "Cricket", label: "Cricket" },
    { value: "Tennis", label: "Tennis" },
    { value: "Swimming", label: "Swimming" },
  ]

  const daysOptions = [
    { value: "monday", label: "Monday" },
    { value: "tuesday", label: "Tuesday" },
    { value: "wednesday", label: "Wednesday" },
    { value: "thursday", label: "Thursday" },
    { value: "friday", label: "Friday" },
    { value: "saturday", label: "Saturday" },
    { value: "sunday", label: "Sunday" },
  ]

  useEffect(() => {
    fetchOnlyCoachData()
      .then((data) => setCoaches(data.coaches))
      .catch((err) => setError(err.message || "An error occurred while fetching coach data"))
  }, [])

  useEffect(() => {
    if (academyData) {
      setFormData({
        name: academyData.name || "",
        address: academyData.address || "",
        locationPin: academyData.locationPin || "",
        timings: academyData.timings || "",
        sports: academyData.sports || [],
        availableDays: academyData.availableDays || [],
        coach: academyData.coach || "",
        pricing: academyData.pricing || "",
        password: "",
        confirmPassword: "",
        hasOwnAcademy: academyData.hasOwnAcademy ?? true,
        id: academyData._id,
      })
    } else {
      setFormData({
        name: "",
        address: "",
        locationPin: "",
        timings: "",
        sports: [],
        availableDays: [],
        coach: "",
        pricing: "",
        password: "",
        confirmPassword: "",
        hasOwnAcademy: true,
      })
    }
  }, [academyData])

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleMultiSelectChange = (field) => (value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value) ? prev[field].filter((item) => item !== value) : [...prev[field], value],
    }))
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setSuccessMessage("")

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match")
      return
    }

    try {
      const payload = {
        ...formData,
        finalRole: "coach",
        hasAcademy: formData.hasOwnAcademy,
      }

      if (academyData) {
        await updateAcademy(payload)
        onUpdateAcademy()
      } else {
        await signup(payload)
        onAddAcademy()
      }

      setSuccessMessage("Academy saved successfully!")
      onClose()
    } catch (err) {
      setError(err.message || "An error occurred")
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="p-6">
        <h2 className="text-2xl font-semibold text-center mb-6">{academyData ? "Edit Academy" : "Add Academy"}</h2>
        <form onSubmit={handleSubmit}>
          <Input label="Academy Name" name="name" value={formData.name} onChange={handleChange} required />
          <Input label="Address" name="address" value={formData.address} onChange={handleChange} required />
          <Input
            label="Location Pin"
            name="locationPin"
            value={formData.locationPin}
            onChange={handleChange}
            required
          />
          <Input label="Timings" name="timings" value={formData.timings} onChange={handleChange} required />
          <MultiSelect
            label="Sports"
            options={sportsOptions}
            value={formData.sports}
            onChange={handleMultiSelectChange("sports")}
          />
          <MultiSelect
            label="Available Days"
            options={daysOptions}
            value={formData.availableDays}
            onChange={handleMultiSelectChange("availableDays")}
          />
          {academyData ? (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">Coach (Owner)</label>
              <div className="p-2 bg-gray-100 rounded-md">{academyData.owner.email}</div>
            </div>
          ) : (
            <Select
              label="Coach (Owner)"
              name="coach"
              value={formData.coach}
              onChange={handleChange}
              options={[
                { value: "", label: "Select a Coach" },
                ...coaches.map((coach) => ({ value: coach.email, label: coach.email })),
              ]}
              required
            />
          )}
          <Input
            label="Pricing"
            name="pricing"
            type="number"
            value={formData.pricing}
            onChange={handleChange}
            required
          />
          {!academyData && (
            <>
              <Input
                label="Password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
              <Input
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
            </>
          )}
          {error && <p className="text-red-500 text-sm mb-4">{error}</p>}
          {successMessage && <p className="text-green-500 text-sm mb-4">{successMessage}</p>}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
            <button type="submit" className="px-4 py-2 bg-[#12143F] text-white rounded-md hover:bg-opacity-90">
              {academyData ? "Update Academy" : "Save Academy"}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  )
}

export default AddAcademyModal

